import {
  isDrawRule,
  MATCH_MINUTES_MAX,
  MATCH_MINUTES_MIN,
  MATCH_PERIODS_MAX,
  MATCH_PERIODS_MIN,
  type MatchDrawRule,
} from "./config.js";
import { ONLINE_MODE_IDS, type OnlineModeId } from "./online.js";
import { DEFAULT_PITCH_SKIN, isPitchSkinId, type PitchSkinId } from "./pitchSkin.js";
import type { WireInputFrame } from "./inputWire.js";
import type { TierId } from "./competitive.js";

// ------------------------------------------------------------ estadisticas mundiales

/**
 * Lo que se puede clasificar. Todo sale de partidos online que el servidor
 * ha arbitrado el mismo (nunca del perfil que manda el cliente). `mmr` es la
 * puntuacion competitiva de la temporada (solo partidos contra personas).
 */
export type WorldMetric = "mmr" | "overall" | "goals" | "assists" | "mvps" | "wins" | "winRate" | "matches";
export const WORLD_METRICS: readonly WorldMetric[] = ["mmr", "overall", "goals", "assists", "mvps", "wins", "winRate", "matches"];
export type WorldPeriod = "week" | "season" | "all";
export const WORLD_PERIODS: readonly WorldPeriod[] = ["week", "season", "all"];
/** Filas por pagina. */
export const WORLD_PAGE = 50;
/** Partidos en el periodo para entrar en las clasificaciones de media y de % de victorias. */
export const WORLD_MIN_MATCHES = 5;

/** Los mismos contadores que la ficha personal (`CareerTotals` del juego): la media se calcula igual. */
export interface WorldTotals {
  matches: number;
  wins: number;
  draws: number;
  losses: number;
  goals: number;
  assists: number;
  mvps: number;
  shots: number;
  passes: number;
  passesOk: number;
  recoveries: number;
  cleanSheets: number;
  touches: number;
  turnovers: number;
  goalsFor: number;
  goalsAgainst: number;
  seconds: number;
}

export interface WorldRow {
  rank: number;
  clientId: string;
  nickname: string;
  value: number;
  matches: number;
  /** Division de la temporada (null en colocacion o sin partidos contra personas). */
  tier: TierId | null;
}

/** Tu sitio. `rank` null si aun no entras (te faltan `needs` partidos). */
export interface WorldMe {
  rank: number | null;
  value: number;
  matches: number;
  /** Mejor que este % de los clasificados. */
  percentile: number | null;
  needs: number;
  above: WorldRow | null;
  below: WorldRow | null;
}

export interface WorldBadge {
  id: string;
  /** Cuantas veces, o el valor que lo gano (un record, una racha). */
  value?: number;
  /** Division, para las insignias de temporada. */
  tier?: TierId;
}

export interface WorldCard {
  clientId: string;
  nickname: string;
  rating: {
    season: string;
    mmr: number;
    games: number;
    wins: number;
    draws: number;
    losses: number;
    peak: number;
    streak: number;
    bestStreak: number;
  };
  totals: WorldTotals;
  season: WorldTotals;
  week: WorldTotals;
  modes: Array<{ modeId: string; matches: number; wins: number; goals: number; assists: number }>;
  recent: Array<{
    at: number;
    modeId: string;
    result: "win" | "draw" | "loss";
    goalsFor: number;
    goalsAgainst: number;
    goals: number;
    assists: number;
    mvp: boolean;
    vsHumans: boolean;
  }>;
  best: { goals: number; biggestWin: number; score: number; winStreak: number };
  /** Puesto en las clasificaciones de siempre (y `mmr` en la de la temporada). */
  ranks: Partial<Record<WorldMetric, number>>;
  badges: WorldBadge[];
  pastSeasons: Array<{ season: string; mmr: number; games: number; rank: number }>;
}

export interface WorldSeasonPodium {
  season: string;
  /** Los tres primeros (con la colocacion hecha), del primero al tercero. */
  podium: Array<{ clientId: string; nickname: string; mmr: number; tier: TierId | null }>;
}

export interface WorldWeekWinner {
  /** Lunes 00:00 UTC de esa semana. */
  weekStart: number;
  clientId: string;
  nickname: string;
  wins: number;
}

export interface WorldRecord {
  id: "goals" | "biggestWin" | "score" | "winStreak" | "mvps" | "assists";
  value: number;
  clientId: string;
  nickname: string;
  at: number;
  modeId?: string;
}

export interface SeatOccupant {
  clientId: string;
  nickname: string;
  /** Su division de la temporada (estadisticas mundiales), si tiene y no se esconde. Solo se pinta. */
  tier?: TierId;
  /**
   * Sigue en la pantalla de resultados del partido anterior: no ha vuelto a la
   * sala. Si el anfitrion arranca otro, pasa al banquillo en vez de dejar a
   * todos esperando a alguien que no esta mirando.
   */
  away?: boolean;
}

/**
 * Lo que hace falta, ademas de la semilla, para simular el MISMO partido.
 *
 * Viaja en `match:start` y en `match:latejoin` y no se lee de la lista de salas:
 * quien entra por un enlace puede no tener la sala en su lista, y un formato o
 * una jornada distintos son un partido distinto. El campo va aqui por lo mismo,
 * aunque solo se vea: dos pistas distintas son dos sitios distintos.
 */
export interface MatchSetup {
  fieldId?: OnlineFieldId;
  minutes?: number;
  periods?: number;
  onDraw?: MatchDrawRule;
}

export interface OnlineRoom {
  id: string;
  name: string;
  modeId: OnlineModeId;
  hostId: string;
  /** playerId → occupant; absent seat = bot. */
  seats: Record<string, SeatOccupant | undefined>;
  status: "lobby" | "playing";
  createdAt: number;
  visibility: RoomVisibility;
  fieldId: OnlineFieldId;
  /** true si la sala exige contraseña (nunca se envía la contraseña). */
  requiresPassword?: boolean;
  /** Sala de partida rápida: sin host humano, arranca sola al llenarse. */
  auto?: boolean;
  /**
   * Jornada semanal que rige esta sala. La decide el SERVIDOR: si cada cliente
   * la dedujera de su reloj, uno con la hora mal jugaria con otras reglas.
   */
  weeklyRuleId?: string;
  /**
   * false = ya no se puede entrar AL PARTIDO en marcha: va tan avanzado que
   * reproducirlo desde cero costaria megas de historial. A la SALA si se
   * entra —al banquillo— y se juega el siguiente. Ausente significa que si.
   */
  joinable?: boolean;
  /**
   * Quien esta en la sala sin asiento: esperando el siguiente partido o a que
   * el anfitrion le meta. Solo viaja a los que estan DENTRO (`room:update`): en
   * la lista general basta con `benchCount`, y mandar veinte nombres por sala a
   * todos los conectados en cada `rooms:sync` seria pagar por nada.
   */
  bench?: SeatOccupant[];
  /** Cuantos esperan en el banquillo. Viaja siempre, tambien en la lista. */
  benchCount?: number;
  /** La sala no admite a nadie mas: ni asiento ni banquillo. */
  full?: boolean;
  /**
   * Cuando arranco el partido en curso. La sala ya no muere al acabar, asi que
   * `createdAt` no dice por que minuto va: una sala puede llevar tres partidos.
   */
  startedAt?: number;
  /**
   * Duracion del partido, en minutos. La elige quien abre la sala.
   *
   * Viaja EN LA SALA y no en la preferencia de cada uno por la misma razon que
   * el campo: `MATCH.DURATION` arranca `timeRemaining`, o sea que es estado de
   * la simulacion. Dos clientes con duraciones distintas terminarian el partido
   * en ticks distintos — que es exactamente el desincronizador que el plan de
   * equipo ya provoco una vez.
   */
  minutes?: number;
  /**
   * Cuantos tiempos y que pasa si empatan. Van con `minutes` porque los tres
   * son el mismo ajuste —el FORMATO— y los tres son estado de la simulacion:
   * si cada cliente jugara los suyos, el partido acabaria en ticks distintos.
   */
  periods?: number;
  onDraw?: MatchDrawRule;
}

/**
 * `link` = sala por invitacion: no sale en ninguna lista y se entra con el id,
 * que hace de secreto. Es lo que permite "copia el enlace y pegalo en el grupo"
 * sin cuentas, sin contrasena y sin buscar en el lobby.
 */
export type RoomVisibility = "public" | "private" | "link";

/**
 * El campo en el que se juega la sala.
 *
 * Lo elige quien la crea y lo ven TODOS: es lo unico de la personalizacion que
 * no puede ser local: si cada cliente pintase su cesped, dos jugadores estarian
 * hablando de sitios distintos, y en una sala montada para jugar con amigos el
 * escenario es la mitad de la gracia.
 *
 * Era un unico valor `"classic"` — un campo (un tipo con un solo miembro) que
 * no elegia nada. Ahora son las mismas pistas que el partido local.
 */
export type OnlineFieldId = PitchSkinId;

export const DEFAULT_ONLINE_FIELD: OnlineFieldId = DEFAULT_PITCH_SKIN;

export function isOnlineFieldId(v: unknown): v is OnlineFieldId {
  return typeof v === "string" && isPitchSkinId(v);
}

/** Numero de tiempos valido. Se valida aqui y no solo en la UI, como `minutes`. */
export function isRoomPeriods(v: unknown): v is number {
  return (
    typeof v === "number" &&
    Number.isInteger(v) &&
    v >= MATCH_PERIODS_MIN &&
    v <= MATCH_PERIODS_MAX
  );
}

/** Duracion valida para una sala: entero, y dentro de los limites del juego. */
export function isRoomMinutes(v: unknown): v is number {
  return (
    typeof v === "number" &&
    Number.isInteger(v) &&
    v >= MATCH_MINUTES_MIN &&
    v <= MATCH_MINUTES_MAX
  );
}

/**
 * Recuento de gente conectada.
 *
 * `playing` NO es `online`: la mayoria de la gente que esta conectada esta
 * mirando el lobby, no jugando. Enseñar solo el total infla la cifra y se nota;
 * enseñar solo los que juegan la hunde cuando hay veinte esperando. Las dos
 * juntas cuentan la verdad, y es la unica pareja de numeros que responde a
 * "¿hay partida?".
 */
export interface OnlinePresence {
  /** Conectados al servidor: jugando, en el lobby o en un menu. */
  online: number;
  /** Dentro de un partido en curso, ahora mismo. */
  playing: number;
  /** Salas vivas: esperando gente o ya jugando. */
  rooms: number;
  /** Partidos en curso. */
  matches: number;
}

/**
 * Una linea del chat de la sala.
 *
 * Los avisos del sistema (`event`) viajan como CODIGO y no como frase: el
 * servidor no sabe en que idioma juega cada uno, asi que la frase la pone el
 * cliente con `t()`. Si el servidor mandara "Ana ha entrado", el que juega en
 * ingles la leeria en castellano.
 */
export interface RoomChatEntry {
  /** Creciente dentro de la sala: sirve de clave y para no repetir. */
  id: number;
  at: number;
  /** clientId de quien escribe; null en los avisos del sistema. */
  from: string | null;
  nickname: string;
  /** Division de quien escribe, si tiene y no se esconde. Solo se pinta. */
  tier?: TierId;
  text?: string;
  event?: RoomChatEvent;
}

export type RoomChatEvent =
  | "joined"
  | "left"
  | "kicked"
  | "host"
  | "benched"
  | "seated"
  | "stopped";

/** Tope de gente en una sala, sumando asientos y banquillo. */
export const MAX_ROOM_MEMBERS = 20;
/** Tope de un mensaje de chat. Una linea que se lee de un vistazo jugando. */
export const MAX_CHAT_CHARS = 140;
/** Lineas que guarda la sala para el que llega. */
export const CHAT_HISTORY = 40;

/**
 * A donde mover a alguien de la sala: un asiento concreto, el mejor hueco
 * libre de un equipo, o `null` para el banquillo.
 */
export type RoomMoveTarget = string | "home" | "away" | null;

/**
 * Un amigo, visto desde tu lista. `status` lo calcula el servidor en vivo.
 *
 * `room*` solo va si esta en una sala: es lo que permite "unirse a Ana" con un
 * toque. Una sala por enlace se enseña igual — sois amigos, y el enlace es
 * justo para eso.
 */
export interface FriendEntry {
  clientId: string;
  nickname: string;
  /** Su division de la temporada, si tiene y no se esconde. */
  tier?: TierId;
  status: "offline" | "online" | "room" | "playing";
  /** Ultima vez que se conecto (ms). Para "hace 3 dias". */
  lastSeen: number;
  /** Amigos desde (ms). */
  since?: number;
  roomId?: string;
  roomName?: string;
  roomMode?: OnlineModeId;
  /** Hay sitio para ti ahora mismo (asiento o banquillo). */
  roomOpen?: boolean;
  roomLocked?: boolean;
  /**
   * Lo que llevais, contado por el ARBITRO del servidor (partidos online que
   * rehizo el mismo): uno contra otro, y juntos en el mismo equipo. Desde tu
   * lado: `vs[0]` son las que le has ganado tu. Sin partidos, no viene.
   */
  vs?: WinDrawLoss;
  with?: WinDrawLoss;
  /** Su grupo, si tiene: para "Unirse al grupo" (abierto) o "Pedir unirse". */
  party?: { id: string; size: number; max: number; open: boolean; leader: boolean };
  /** Cuando empezo el partido que esta jugando (ms), para "min 3". */
  playingSince?: number;
}

/**
 * - `sent`: le ha llegado.
 * - `playing`: le ha llegado, pero esta en un partido: la vera al acabar.
 * - `offline`: no esta conectado; no le llega nada.
 * - `here`: ya esta en tu sala.
 * - `declined`: ha dicho "ahora no".
 * - `slow`: demasiadas invitaciones seguidas.
 */
export type InviteResult = "sent" | "playing" | "offline" | "here" | "declined" | "slow";

/**
 * Lo de las invitaciones a sala, mas `full` (el grupo no admite a nadie mas)
 * y `asked` (tu peticion de entrar le ha llegado al lider).
 */
export type PartyResult = InviteResult | "full" | "asked";

export type PartyMemberStatus = "online" | "room" | "playing" | "away";

export interface PartyMemberView {
  clientId: string;
  nickname: string;
  tier?: TierId;
  /** `away`: se le cayo la conexion y aun tiene un rato para volver. */
  status: PartyMemberStatus;
}

export interface PartyView {
  id: string;
  leaderId: string;
  open: boolean;
  max: number;
  members: PartyMemberView[];
  /** Invitados que aun no han contestado (se ven como hueco "esperando"). */
  invited: FriendRequestEntry[];
  /** Solo al lider: quien pide entrar. */
  requests?: FriendRequestEntry[];
}

/** Victorias, empates y derrotas. */
export type WinDrawLoss = [wins: number, draws: number, losses: number];

export interface FriendRequestEntry {
  clientId: string;
  nickname: string;
}

/**
 * Una fila de la clasificacion entre amigos. Sale del perfil ONLINE que cada
 * uno tiene guardado: contra personas, que es lo que se compara.
 */
export interface FriendBoardRow {
  clientId: string;
  nickname: string;
  me: boolean;
  overall: number;
  level: number;
  /** Puntos de ESTA semana (0 si su perfil es de una semana anterior). */
  weekPoints: number;
  weekMatches: number;
  matches: number;
  wins: number;
  goals: number;
  assists: number;
  mvps: number;
}

/** Tope de un mensaje del chat del lobby: el mismo que el de sala. */
export const LOBBY_CHAT_HISTORY = 50;

/** Un mensaje privado entre amigos. */
export interface DirectMessageEntry {
  id: number;
  from: string;
  text: string;
  at: number;
}

/** Resultado de pedir amistad. */
export type FriendAddResult =
  | "sent"
  | "accepted"
  | "already"
  | "pending"
  | "self"
  | "not_found"
  | "limit"
  | "slow"
  | "blocked";

/** Client → server messages. */
/** Peticiones de cuenta. Separadas del juego: no tocan ninguna partida. */
export type AccountMessage =
  /** Sube la carrera para que sobreviva a cambiar de navegador. */
  /**
   * `side`: que perfil es. Online (contra personas) y offline (contra la IA y la
   * carrera) se llevan por separado; sin `side` es el online, que es lo que
   * mandaban los clientes de antes de la separacion.
   */
  | { type: "profile:push"; profile: string; side?: "online" | "offline" }
  /** La carrera offline, las equipaciones ganadas y el salon, en su hueco de la cuenta. */
  | { type: "career:push"; save: string }
  /** Una clasificacion mundial. `friends`: solo tus amigos y tu. */
  | {
      type: "world:board";
      metric: WorldMetric;
      period: WorldPeriod;
      mode?: OnlineModeId;
      friends?: boolean;
      offset?: number;
    }
  /** La ficha publica de alguien (o la tuya). */
  | { type: "world:card"; clientId: string }
  | { type: "world:records" }
  /** No salir en las clasificaciones mundiales (ni en fichas de otros). */
  | { type: "world:visibility"; hidden: boolean }
  /** El salon de campeones: podios de temporadas pasadas y ganadores de cada semana. */
  | { type: "world:champions" }
  /** Pide un codigo para llevarse la cuenta a otro dispositivo. */
  | { type: "account:recovery" }
  /** Canjea un codigo emitido en otro dispositivo. */
  | { type: "account:redeem"; code: string }
  /** Enlaza (o recupera) la cuenta con Google. `credential` es el ID token. */
  | { type: "account:google"; credential: string };

export type ClientMessage =
  | AccountMessage
  /**
   * `token` es la credencial: el server lo emite en `welcome` y es lo UNICO que
   * demuestra ser el dueno de una identidad. Aqui NO se manda clientId: viaja en
   * los asientos de cada sala, o sea que es publico, y aceptarlo como prueba
   * dejaba que cualquiera echase a su dueno de la partida.
   *
   * `roomId` = la partida que el cliente cree estar jugando, para validarla al reconectar.
   */
  | {
      type: "hello";
      token?: string;
      nickname: string;
      roomId?: string;
      /**
       * Huella de la simulacion del cliente (`packages/shared/scripts/simVersion.mjs`).
       * Sin ella —un cliente de antes del 2026-09-29— o distinta de la del
       * servidor, se cierra con `CLOSE_OUTDATED`. "dev" pasa siempre.
       */
      version?: string;
    }
  | {
      type: "room:create";
      name: string;
      modeId: OnlineModeId;
      nickname: string;
      visibility?: RoomVisibility;
      fieldId?: OnlineFieldId;
      password?: string;
      /** Minutos de partido. Lo elige quien abre la sala; ver `OnlineRoom`. */
      minutes?: number;
      periods?: number;
      onDraw?: MatchDrawRule;
    }
  | { type: "room:join"; roomId: string; nickname: string; password?: string }
  | { type: "room:leave"; roomId: string }
  /**
   * Cambiarse a otro asiento de la sala (elegir bando). Solo en el lobby: a
   * mitad de partido los asientos estan atados a la simulacion en curso.
   */
  | { type: "room:seat"; roomId: string; playerId: string }
  | { type: "room:start"; roomId: string }
  /**
   * Mover a alguien de la sala. El anfitrion mueve a cualquiera; los demas
   * solo a si mismos, y solo con la sala en el lobby (a un hueco libre o al
   * banquillo). Todo se valida en el servidor.
   */
  | { type: "room:move"; roomId: string; clientId: string; to: RoomMoveTarget }
  /** Echar a alguien de la sala. Solo el anfitrion; no puede volver a ESA sala. */
  | { type: "room:kick"; roomId: string; clientId: string }
  /** Parar el partido en curso y volver todos a la sala. Solo el anfitrion. */
  | { type: "room:stop"; roomId: string }
  | { type: "room:chat"; roomId: string; text: string }
  /**
   * "He visto el pitido final en el tick N". El servidor no simula, asi que es
   * la unica forma de que sepa que el partido acabo y la sala vuelva al lobby.
   * No basta uno: ver `reportEnd` en la sesion.
   */
  | { type: "match:end"; roomId: string; tick: number }
  | { type: "room:list" }
  /** Entra en la cola del modo: el server busca sala, la crea si hace falta y la arranca. */
  | { type: "quickplay"; modeId: OnlineModeId; nickname: string }
  | { type: "quickplay:cancel" }
  | { type: "match:ready"; roomId: string }
  /**
   * El anfitrion que jugaba solo confirma en que tick se ha parado: todo lo
   * anterior ya lo ha mandado, y desde este tick juega por lockstep con los
   * que entran. Ver `match:handoff`.
   */
  | { type: "match:handoff:ack"; roomId: string; tick: number }
  | { type: "input:submit"; roomId: string; tick: number; playerId: string; frame: WireInputFrame }
  /** Huella del estado simulado en un tick, para detectar divergencias. */
  | { type: "sync:hash"; roomId: string; tick: number; hash: string }
  /**
   * Denunciar a alguien de tu sala.
   *
   * Sin motivo en texto libre a proposito: un campo de texto que llega al
   * servidor es otra superficie que moderar, y para lo que hace falta —saber a
   * quien mirar— basta con el id.
   */
  | { type: "player:report"; roomId: string; playerId: string }
  /** Pedir la lista de amigos (y el codigo propio). */
  | { type: "friends:get" }
  /**
   * Pedir amistad por codigo, por apodo (son unicos), o a alguien que ves (sala, chat) por su id.
   *
   * `viaRoom`: has llegado por el enlace de invitacion de esa sala, y el
   * enlace traia el codigo de quien lo compartio. Si los dos estais dentro,
   * sois amigos sin mas: el uno paso el enlace y el otro lo abrio.
   */
  | { type: "friends:add"; code?: string; clientId?: string; nickname?: string; viaRoom?: string }
  /** Bloquear: rompe la amistad y deja de recibir sus solicitudes, invitaciones y privados. */
  | { type: "friends:block"; clientId: string }
  | { type: "friends:unblock"; clientId: string }
  /** Privado a un amigo. */
  | { type: "dm:send"; to: string; text: string }
  /** Historial con un amigo (los ultimos, o los anteriores a `before`). */
  | { type: "dm:history"; with: string; before?: number }
  /** He leido hasta `upTo` en la conversacion con `with`. */
  | { type: "dm:read"; with: string; upTo: number }
  | { type: "friends:respond"; clientId: string; accept: boolean }
  /** Quitar a un amigo o cancelar una solicitud. */
  | { type: "friends:remove"; clientId: string }
  /** La ficha online de un amigo (solo amigos: a nadie mas). */
  | { type: "friends:profile"; clientId: string }
  /** Clasificacion de la semana entre tus amigos y tu. */
  | { type: "friends:board" }
  /** Invitar a un amigo a la sala en la que estas. */
  | { type: "room:invite"; roomId: string; clientId: string }
  /** "Ahora no" a una invitacion: se le dice a quien invito, para que no espere. */
  | { type: "room:invite:decline"; roomId: string; from: string }
  /*
   * GRUPO (como la escuadra de Fortnite): amigos que van juntos. El lider
   * entra en una sala o pulsa JUGAR y el resto va detras.
   */
  /** Invitar a un amigo a tu grupo (si no tienes, se crea contigo de lider). */
  | { type: "party:invite"; clientId: string }
  /** Entrar: con invitacion, o si el grupo esta abierto y eres amigo de alguien de dentro. */
  | { type: "party:join"; partyId: string }
  /** Pedir entrar a un grupo cerrado: decide el lider. */
  | { type: "party:request"; partyId: string }
  /** El lider contesta a una peticion. */
  | { type: "party:answer"; clientId: string; accept: boolean }
  /** "Ahora no" a una invitacion al grupo. */
  | { type: "party:decline"; partyId: string }
  | { type: "party:leave" }
  | { type: "party:kick"; clientId: string }
  /** Pasar el mando. */
  | { type: "party:promote"; clientId: string }
  /** Abierto a amigos, o solo con invitacion. */
  | { type: "party:open"; open: boolean }
  /** Entrar y salir del chat del lobby (mientras miras la lista de salas). */
  | { type: "lobby:enter" }
  | { type: "lobby:leave" }
  | { type: "lobby:chat"; text: string }
  /**
   * Cambiar de apodo. Los apodos son UNICOS: el servidor lo reserva si nadie
   * lo tiene y contesta con `nickname:result`.
   */
  | { type: "nickname:set"; nickname: string }
  /** ¿Esta libre? Para decirlo mientras se escribe, antes de pulsar nada. */
  | { type: "nickname:check"; nickname: string }
  | { type: "ping"; t: number };

/** Server → client messages. */
export type ServerMessage =
  /** `token` es secreto: se guarda y se reenvia en el siguiente `hello`. */
  | {
      type: "welcome";
      clientId: string;
      token: string;
      /**
       * El apodo tal y como ha quedado en el servidor.
       *
       * Va de vuelta porque el server puede cambiarlo —lo recorta, y lo
       * sustituye si no pasa el filtro—, y un jugador al que le cambian el
       * nombre sin decirle nada cree que hay un fallo. El cliente lo guarda,
       * asi que la correccion se queda.
       */
      nickname: string;
      /**
       * Carrera guardada en el server, o null si esta cuenta no ha subido
       * ninguna. El server la almacena como texto y NO la interpreta: quien
       * decide si vale mas la local o la del server es el cliente, que es donde
       * vive la logica de carrera. El server es almacen, no juez.
       */
      profile: string | null;
      /** El perfil offline (contra la IA y la carrera), o null. */
      profileOffline?: string | null;
      /** La carrera offline guardada (ver `career:push`), o null. */
      career?: string | null;
      /** Ha pedido no salir en las clasificaciones mundiales. */
      worldHidden?: boolean;
      /** Equipaciones de competicion ganadas (las da el servidor: ver `COMPETITIVE_KITS`). */
      worldKits?: string[];
      /**
       * El apodo que se pidio en `hello` y no se pudo dar porque ya es de otro.
       * El cliente se queda con `nickname` y le pide al jugador que elija.
       */
      nicknameTaken?: string;
      /** Ya tiene codigo de recuperacion emitido. */
      hasRecovery: boolean;
      /** Correo de la cuenta de Google enlazada, o null. */
      googleEmail: string | null;
      /**
       * Client ID de Google, o null si el server no lo tiene configurado.
       *
       * Lo manda el SERVER para que solo haya un sitio donde configurarlo. Si
       * el cliente lo llevara por su cuenta podrian desincronizarse y el `aud`
       * del token no cuadraria — un fallo silencioso y odioso de depurar. Es
       * publico por diseño: va en la pagina de cualquier web con Google.
       */
      googleClientId: string | null;
    }
  /** Codigo de recuperacion recien emitido. Se enseña UNA vez. */
  | { type: "account:recovery"; code: string }
  /** Canje correcto: el cliente cambia de identidad a la recuperada. */
  | {
      type: "account:redeemed";
      clientId: string;
      token: string;
      nickname: string;
      profile: string | null;
      /** El perfil offline (contra la IA y la carrera), o null. */
      profileOffline?: string | null;
      /** La carrera offline guardada, o null. */
      career?: string | null;
      /** Correo enlazado, para poder decir "conectado como…". */
      googleEmail: string | null;
    }
  | { type: "rooms:sync"; rooms: OnlineRoom[] }
  /**
   * Cuanta gente hay AHORA MISMO, en vivo.
   *
   * Es la señal de que el sitio esta vivo, y va aparte de `rooms:sync` porque
   * cambia por motivos que no tocan ninguna sala —alguien abre el juego, alguien
   * lo cierra— y porque hay que poder mandarla sola, cada pocos segundos, sin
   * reenviar la lista entera de salas a todo el mundo.
   */
  | { type: "presence"; presence: OnlinePresence }
  | { type: "room:update"; room: OnlineRoom }
  | ({
      type: "match:start";
      roomId: string;
      seed: number;
      modeId: OnlineModeId;
      seats: Record<string, SeatOccupant>;
      startTick: number;
      /** Jornada con la que se juega, si toca. */
      weeklyRuleId?: string;
      /**
       * Hay UNA sola persona: se juega en local, sin el retraso del lockstep, y
       * el servidor apunta tick a tick lo que se jugo. Si entra alguien, el
       * servidor pide `match:handoff` y desde ahi es lockstep normal.
       */
      solo?: boolean;
    } & MatchSetup)
  | { type: "tick:bundle"; roomId: string; tick: number; frames: Record<string, WireInputFrame> }
  | ({
      type: "match:latejoin";
      roomId: string;
      seed: number;
      modeId: OnlineModeId;
      playerId: string;
      seats: Record<string, SeatOccupant>;
      tick: number;
      history: Array<{ tick: number; frames: Record<string, WireInputFrame> }>;
      weeklyRuleId?: string;
    } & MatchSetup)
  /**
   * Al anfitrion que juega solo: alguien quiere entrar. Tiene que parar en el
   * tick en que va, contestar `match:handoff:ack` con ese tick y seguir por
   * lockstep. El servidor no puede elegir el tick por el: el anfitrion va por
   * delante de lo que el servidor ha recibido, y cualquier tick que no sea el
   * suyo deja a quien entra reproduciendo un partido que no es el que se jugo.
   */
  | { type: "match:handoff"; roomId: string }
  | { type: "peer:joined"; roomId: string; playerId: string; nickname: string; seats: Record<string, SeatOccupant> }
  | { type: "peer:left"; roomId: string; playerId: string; seats: Record<string, SeatOccupant> }
  /** El input llego para un tick ya emitido: el cliente debe resincronizar y subir su margen. */
  | { type: "input:late"; roomId: string; serverTick: number; lateBy: number }
  /** Dos clientes de la sala simularon estados distintos en el mismo tick. */
  | { type: "sync:desync"; roomId: string; tick: number }
  /*
   * Aqui vivia `quickplay:status`: "faltan 2 jugadores, empieza en 18 s".
   *
   * Se fue con la cola. Ya no hay nada que informar porque ya no hay espera: se
   * pulsa JUGAR y la sala arranca en el mismo tick, con bots en los huecos y la
   * puerta abierta para el que llegue.
   */
  | { type: "match:over"; roomId: string; reason: "finished" | "abandoned" | "stopped" }
  /** Lineas nuevas del chat; al entrar en la sala llega el historial entero. */
  | { type: "room:chat"; roomId: string; entries: RoomChatEntry[] }
  /** Te han echado de la sala. */
  | { type: "room:kicked"; roomId: string }
  /** El anfitrion te ha sacado del partido en curso al banquillo. */
  | { type: "room:benched"; roomId: string }
  | {
      type: "friends:sync";
      /** Tu codigo de amigo, para compartirlo. */
      code: string;
      friends: FriendEntry[];
      incoming: FriendRequestEntry[];
      outgoing: FriendRequestEntry[];
      /** A quien tienes bloqueado (para poder desbloquear y para ocultar sus mensajes). */
      blocked?: FriendRequestEntry[];
    }
  /** Algo que merece un aviso: te han pedido amistad, o te la han aceptado. */
  | { type: "friends:notice"; kind: "request" | "accepted"; from: FriendRequestEntry }
  /** Resultado de pedir amistad, para decirselo a quien la pidio. */
  | {
      type: "friends:result";
      result: FriendAddResult;
    }
  | {
      type: "room:invited";
      roomId: string;
      roomName: string;
      modeId: OnlineModeId;
      from: FriendRequestEntry;
      /** Personas dentro y sitios de campo, para "3/6" en el aviso. */
      humans?: number;
      capacity?: number;
    }
  /** Que ha pasado con tu invitacion, para quien invito. */
  | { type: "room:invite:result"; clientId: string; result: InviteResult }
  /** Tu grupo, entero, cada vez que cambia. `null`: no estas en ninguno (y por que). */
  | { type: "party:sync"; party: PartyView | null; reason?: "left" | "kicked" | "empty" }
  | { type: "party:invited"; partyId: string; from: FriendRequestEntry; size: number }
  /** Al lider: alguien pide entrar. */
  | { type: "party:requested"; from: FriendRequestEntry }
  /** Que ha pasado con tu invitacion o tu peticion. */
  | { type: "party:result"; clientId: string; result: PartyResult }
  /**
   * El lider ha entrado en una sala: vente. Ya llevas un pase (entras aunque
   * tenga contraseña) y te sientas con tu grupo.
   */
  | { type: "party:follow"; roomId: string; from: FriendRequestEntry }
  | { type: "friends:profile"; clientId: string; nickname: string; profile: string | null }
  | { type: "friends:board"; week: string; rows: FriendBoardRow[] }
  /**
   * Lineas del chat del lobby. `here` = cuantos lo miran.
   *
   * `snapshot` marca el historial entero que llega al entrar: SUSTITUYE lo que
   * el cliente tuviera. Sin la marca se mezclaba por id, y tras reiniciarse el
   * servidor los ids vuelven a empezar: las lineas nuevas se descartaban por
   * "ya vistas" y ese chat se quedaba congelado en el pasado.
   */
  | { type: "lobby:chat"; entries: RoomChatEntry[]; here: number; snapshot?: boolean }
  /** Respuesta a `nickname:set`. `nickname` es el que tienes AHORA (el nuevo, o el de antes). */
  | { type: "nickname:result"; ok: boolean; nickname: string; reason?: NicknameProblem }
  | { type: "nickname:check"; nickname: string; status: NicknameStatus }
  /** Un privado nuevo, a los DOS lados de la conversacion (`with` = el otro). */
  | { type: "dm:message"; with: string; entry: DirectMessageEntry }
  /** Historial de una conversacion, del mas viejo al mas nuevo. `more` = hay anteriores. */
  | { type: "dm:history"; with: string; entries: DirectMessageEntry[]; more: boolean }
  /** Sin leer por amigo, al conectar. */
  | { type: "dm:unread"; counts: Record<string, number> }
  | { type: "pong"; t: number }
  | {
      type: "world:board";
      metric: WorldMetric;
      period: WorldPeriod;
      mode?: OnlineModeId;
      friends?: boolean;
      offset: number;
      total: number;
      rows: WorldRow[];
      me: WorldMe | null;
      /** Temporada en curso ("2026-09") y cuando acaban ella y la semana. */
      season: string;
      seasonEndsAt: number;
      weekEndsAt: number;
    }
  | { type: "world:card"; clientId: string; card: WorldCard | null }
  | { type: "world:records"; records: WorldRecord[] }
  | { type: "world:visibility"; hidden: boolean }
  | { type: "world:champions"; seasons: WorldSeasonPodium[]; weeks: WorldWeekWinner[] }
  | { type: "error"; code: string; message: string };

/** Tope de bytes por mensaje entrante. Un input frame ronda los 200 bytes. */
export const MAX_MESSAGE_BYTES = 16 * 1024;

/*
 * Topes de los textos que escribe el jugador.
 *
 * Viven AQUI, en el validador, y no en cada manejador. El apodo se recortaba a
 * 16 en `hello` y en `quickplay`... pero no en `room:create` ni en `room:join`,
 * que lo pasaban en crudo al registro. El nombre de sala no se recortaba en
 * ningun sitio. El unico techo real era `MAX_MESSAGE_BYTES`, o sea 16 KB de
 * apodo — y ese apodo se reparte a TODOS los conectados en cada `rooms:sync`.
 *
 * Puestos aqui no hay puerta que se los pueda saltar, ni la que se añada
 * mañana. Son techos de seguridad, generosos a proposito: la interfaz ya limita
 * a menos (apodo 16, sala 24, contraseña 32) y quien normaliza al valor bonito
 * sigue siendo el manejador. Lo que esto impide es el abuso, no la holgura.
 */
export const MAX_NICKNAME_CHARS = 32;
/** Lo que se ve de un apodo. El servidor recorta aqui; la interfaz tambien. */
export const NICKNAME_MAX_LENGTH = 16;

/** Por que no se puede usar un apodo. */
export type NicknameProblem = "taken" | "invalid" | "slow";
/** Como esta un apodo que se consulta: libre, de otro, no valido, o ya es el tuyo. */
export type NicknameStatus = "free" | "taken" | "invalid" | "yours";
export const MAX_ROOM_NAME_CHARS = 48;
export const MAX_ROOM_PASSWORD_CHARS = 64;
/** Ids de sala y de asiento: los emite el server, nadie necesita mas. */
export const MAX_ID_CHARS = 64;
/**
 * Tope de la carrera serializada. Por debajo de `MAX_MESSAGE_BYTES` para que
 * quepa el resto del mensaje.
 *
 * Una carrera real ronda los 4-6 KB, y todo lo que podria crecer esta topado en
 * origen: `recent` a 12 partidos y `partners` a `PARTNER_LIMIT`. Aun asi el
 * server cuenta `profilesSaved`, porque el sintoma de pasarse seria un perfil
 * que deja de guardarse EN SILENCIO.
 */
export const MAX_PROFILE_CHARS = 12 * 1024;

/**
 * Tope de la carrera offline guardada en la cuenta (carrera + equipaciones
 * ganadas + salon de la fama), su propio hueco. Por debajo de
 * `MAX_MESSAGE_BYTES`, que NO se sube por esto: el cliente compacta (los clubes
 * salen de la semilla, el movil viaja recortado) y una vida entera de 17 a 35
 * cabe con sitio. Lo vigila `tests/careerCloud.test.ts`.
 */
export const MAX_CAREER_SAVE_CHARS = 14 * 1024;

/**
 * Codigo de cierre cuando la MISMA cuenta entra por otro socket (otra pestaña,
 * otro dispositivo): el servidor se queda con el nuevo y cierra el viejo.
 *
 * El cliente no debe reconectar solo al recibirlo. Hasta el 2026-09-27 lo
 * trataba como un corte de red: volvia al segundo, echaba al otro, y el otro
 * hacia lo mismo — "EN LÍNEA" / "RECONECTANDO" para siempre en las dos
 * pestañas, y el asiento de un partido saltando de una a otra.
 */
export const CLOSE_REPLACED = 4000;

/**
 * Codigo de cierre cuando el cliente trae OTRA simulacion que el servidor (ver
 * `hello.version`). Tampoco se reconecta al momento: si la vieja es la del
 * cliente, reconectar no arregla nada —hay que recargar la pagina—, y si es la
 * del servidor, se arregla sola cuando este se despliegue, asi que se reintenta
 * despacio (`OUTDATED_RETRY_MS`).
 *
 * Sin esto, tras cada despliegue una pestaña abierta de antes entraba en salas
 * con la simulacion nueva y el partido se separaba en el primer gol.
 */
export const CLOSE_OUTDATED = 4001;
export const OUTDATED_RETRY_MS = 60_000;

/** ¿Pueden jugar juntos este cliente y este servidor? "dev" en cualquiera de los dos pasa. */
export function simVersionsMatch(client: string | undefined, server: string): boolean {
  if (server === "dev" || client === "dev") return true;
  return client === server;
}

/**
 * Limite de ritmo por conexion (cubo de fichas).
 *
 * `maxPayload` acota el tamano de cada mensaje pero no la frecuencia, y el event
 * loop lo comparten TODAS las partidas del proceso: un solo cliente que inunda
 * le baja el tick a todo el mundo.
 *
 * El techo sostenido es el doble del ritmo legitimo (60 inputs/s mas pings y
 * hashes), y la capacidad cubre la rafaga real: cuando el margen de lockstep
 * sube de golpe, `submitLocal` manda hasta LOCKSTEP_MAX_FILL mensajes seguidos.
 */
export const RATE_LIMIT = {
  /** Mensajes acumulables en rafaga. */
  BURST_MESSAGES: 240,
  /** Mensajes por segundo en regimen sostenido. */
  MESSAGES_PER_SEC: 120,
  /** Bytes acumulables en rafaga. */
  BURST_BYTES: 256 * 1024,
  /** Bytes por segundo en regimen sostenido. */
  BYTES_PER_SEC: 64 * 1024,
  /**
   * Mensajes descartados seguidos antes de cerrar la conexion. Un pico aislado
   * se absorbe descartando; pasarse de forma sostenida es abuso.
   */
  MAX_VIOLATIONS: 60,
} as const;

type Rec = Record<string, unknown>;

const isStr = (v: unknown): v is string => typeof v === "string";
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const isTick = (v: unknown): v is number => isNum(v) && Number.isInteger(v) && v >= 0;
const optStr = (v: unknown): boolean => v === undefined || isStr(v);
/** Cadena con techo. Todo texto que escribe el jugador pasa por aqui. */
const isText = (v: unknown, max: number): v is string => isStr(v) && v.length <= max;
const optText = (v: unknown, max: number): boolean => v === undefined || isText(v, max);

function isWireInput(v: unknown): v is WireInputFrame {
  if (!v || typeof v !== "object") return false;
  const f = v as Rec;
  const move = f.move as Rec | undefined;
  if (!move || typeof move !== "object" || !isNum(move.x) || !isNum(move.y)) return false;
  if (!Array.isArray(f.held) || !Array.isArray(f.pressed)) return false;
  if (f.held.length > 16 || f.pressed.length > 16) return false;
  if (!f.held.every(isStr) || !f.pressed.every(isStr)) return false;
  if (f.kickPower !== undefined && !isNum(f.kickPower)) return false;
  if (!optStr(f.kickKind) || !optStr(f.shotStyle)) return false;
  return true;
}

/**
 * Valida forma y tipos. Antes era un `as ClientMessage`: cualquier campo podia
 * llegar con el tipo que fuera y reventaba dentro de la logica de sala.
 */
export function parseClientMessage(raw: string): ClientMessage | null {
  if (raw.length > MAX_MESSAGE_BYTES) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const m = parsed as Rec;

  switch (m.type) {
    /*
     * Cuentas. El perfil se acota aparte de `MAX_MESSAGE_BYTES` porque es lo
     * unico que puede crecer con el uso: si un dia la carrera guarda mas cosas,
     * el tope tiene que saltar aqui y no en el limite generico de mensajes.
     */
    case "profile:push":
      return isStr(m.profile) &&
        (m.side === undefined || m.side === "online" || m.side === "offline") &&
        (m.profile as string).length <= MAX_PROFILE_CHARS
        ? (m as ClientMessage)
        : null;
    case "world:board":
      return (WORLD_METRICS as readonly string[]).includes(m.metric as string) &&
        (WORLD_PERIODS as readonly string[]).includes(m.period as string) &&
        (m.mode === undefined || (ONLINE_MODE_IDS as readonly string[]).includes(m.mode as string)) &&
        (m.friends === undefined || typeof m.friends === "boolean") &&
        (m.offset === undefined || (Number.isSafeInteger(m.offset) && (m.offset as number) >= 0 && (m.offset as number) <= 100_000))
        ? (m as ClientMessage)
        : null;
    case "world:card":
      return isText(m.clientId, MAX_ID_CHARS) ? (m as ClientMessage) : null;
    case "world:records":
      return m as ClientMessage;
    case "world:visibility":
      return typeof m.hidden === "boolean" ? (m as ClientMessage) : null;
    case "world:champions":
      return m as ClientMessage;
    case "career:push":
      return isStr(m.save) && (m.save as string).length <= MAX_CAREER_SAVE_CHARS ? (m as ClientMessage) : null;
    case "account:recovery":
      return m as ClientMessage;
    case "account:redeem":
      return isStr(m.code) && (m.code as string).length <= 64 ? (m as ClientMessage) : null;
    case "account:google":
      // Un ID token de Google ronda 1 KB; 4 KB deja margen de sobra sin
      // convertir este mensaje en un vector para saturar el parser.
      return isStr(m.credential) && (m.credential as string).length <= 4096
        ? (m as ClientMessage)
        : null;
    case "hello":
      return isText(m.nickname, MAX_NICKNAME_CHARS) &&
        optText(m.token, MAX_ID_CHARS) &&
        optText(m.roomId, MAX_ID_CHARS) &&
        optText(m.version, MAX_ID_CHARS)
        ? (m as ClientMessage)
        : null;
    case "room:create":
      // modeId se valida contra la lista real: un modo inventado revienta
      // dentro de fieldSeatsForMode (MATCH_MODES[x].formation de undefined).
      return isText(m.name, MAX_ROOM_NAME_CHARS) &&
        (ONLINE_MODE_IDS as readonly string[]).includes(m.modeId as string) &&
        isText(m.nickname, MAX_NICKNAME_CHARS) &&
        optStr(m.visibility) &&
        // El campo se valida contra la lista real: uno inventado dejaria a la
        // sala pintando `undefined` en todos los clientes.
        (m.fieldId === undefined || isOnlineFieldId(m.fieldId)) &&
        // La duracion tambien se valida aqui, no solo en la UI: es estado de la
        // simulacion, y un valor absurdo desde un cliente modificado dejaria a
        // la sala con un reloj que no termina nunca.
        (m.minutes === undefined || isRoomMinutes(m.minutes)) &&
        (m.periods === undefined || isRoomPeriods(m.periods)) &&
        (m.onDraw === undefined || isDrawRule(m.onDraw)) &&
        // Y la contrasena con tope de longitud, no `optStr` a secas: `room:join`
        // la valida asi al otro lado, y una sala creada con una clave mas larga
        // de lo que el `join` acepta seria una sala en la que no puede entrar
        // nadie, ni siquiera quien la creo.
        optText(m.password, MAX_ROOM_PASSWORD_CHARS)
        ? (m as ClientMessage)
        : null;
    case "room:join":
      return isText(m.roomId, MAX_ID_CHARS) &&
        isText(m.nickname, MAX_NICKNAME_CHARS) &&
        optText(m.password, MAX_ROOM_PASSWORD_CHARS)
        ? (m as ClientMessage)
        : null;
    case "room:leave":
    case "room:start":
    case "match:ready":
      return isText(m.roomId, MAX_ID_CHARS) ? (m as ClientMessage) : null;
    case "match:handoff:ack":
      return isText(m.roomId, MAX_ID_CHARS) && isTick(m.tick) ? (m as ClientMessage) : null;
    case "room:seat":
      return isText(m.roomId, MAX_ID_CHARS) && isText(m.playerId, MAX_ID_CHARS)
        ? (m as ClientMessage)
        : null;
    case "room:stop":
      return isText(m.roomId, MAX_ID_CHARS) ? (m as ClientMessage) : null;
    case "room:move":
      return isText(m.roomId, MAX_ID_CHARS) &&
        isText(m.clientId, MAX_ID_CHARS) &&
        (m.to === null || isText(m.to, MAX_ID_CHARS))
        ? (m as ClientMessage)
        : null;
    case "room:kick":
      return isText(m.roomId, MAX_ID_CHARS) && isText(m.clientId, MAX_ID_CHARS)
        ? (m as ClientMessage)
        : null;
    case "room:chat":
      // El tope va con holgura sobre MAX_CHAT_CHARS: el servidor recorta al
      // valor bonito; esto solo corta el abuso.
      return isText(m.roomId, MAX_ID_CHARS) && isText(m.text, MAX_CHAT_CHARS * 2)
        ? (m as ClientMessage)
        : null;
    case "match:end":
      return isText(m.roomId, MAX_ID_CHARS) && isTick(m.tick) ? (m as ClientMessage) : null;
    case "friends:profile":
      return isText(m.clientId, MAX_ID_CHARS) ? (m as ClientMessage) : null;
    case "friends:board":
    case "friends:get":
    case "lobby:enter":
    case "lobby:leave":
      return m as ClientMessage;
    case "friends:add":
      return (m.code === undefined || isText(m.code, 24)) &&
        (m.clientId === undefined || isText(m.clientId, MAX_ID_CHARS)) &&
        (m.nickname === undefined || isText(m.nickname, MAX_NICKNAME_CHARS)) &&
        (m.viaRoom === undefined || isText(m.viaRoom, MAX_ID_CHARS)) &&
        (m.code !== undefined || m.clientId !== undefined || m.nickname !== undefined)
        ? (m as ClientMessage)
        : null;
    case "friends:block":
    case "friends:unblock":
      return isText(m.clientId, MAX_ID_CHARS) ? (m as ClientMessage) : null;
    case "dm:send":
      return isText(m.to, MAX_ID_CHARS) && isText(m.text, MAX_CHAT_CHARS * 2) ? (m as ClientMessage) : null;
    case "dm:history":
      return isText(m.with, MAX_ID_CHARS) && (m.before === undefined || isTick(m.before)) ? (m as ClientMessage) : null;
    case "dm:read":
      return isText(m.with, MAX_ID_CHARS) && isTick(m.upTo) ? (m as ClientMessage) : null;
    case "friends:respond":
      return isText(m.clientId, MAX_ID_CHARS) && typeof m.accept === "boolean" ? (m as ClientMessage) : null;
    case "friends:remove":
      return isText(m.clientId, MAX_ID_CHARS) ? (m as ClientMessage) : null;
    case "room:invite":
      return isText(m.roomId, MAX_ID_CHARS) && isText(m.clientId, MAX_ID_CHARS) ? (m as ClientMessage) : null;
    case "room:invite:decline":
      return isText(m.roomId, MAX_ID_CHARS) && isText(m.from, MAX_ID_CHARS) ? (m as ClientMessage) : null;
    case "party:invite":
    case "party:kick":
    case "party:promote":
      return isText(m.clientId, MAX_ID_CHARS) ? (m as ClientMessage) : null;
    case "party:join":
    case "party:request":
    case "party:decline":
      return isText(m.partyId, MAX_ID_CHARS) ? (m as ClientMessage) : null;
    case "party:answer":
      return isText(m.clientId, MAX_ID_CHARS) && typeof m.accept === "boolean" ? (m as ClientMessage) : null;
    case "party:leave":
      return m as ClientMessage;
    case "party:open":
      return typeof m.open === "boolean" ? (m as ClientMessage) : null;
    case "lobby:chat":
      return isText(m.text, MAX_CHAT_CHARS * 2) ? (m as ClientMessage) : null;
    case "nickname:set":
    case "nickname:check":
      return isText(m.nickname, MAX_NICKNAME_CHARS) ? (m as ClientMessage) : null;
    case "room:list":
    case "quickplay:cancel":
      return m as ClientMessage;
    case "quickplay":
      return (ONLINE_MODE_IDS as readonly string[]).includes(m.modeId as string) &&
        isText(m.nickname, MAX_NICKNAME_CHARS)
        ? (m as ClientMessage)
        : null;
    case "input:submit":
      return isText(m.roomId, MAX_ID_CHARS) &&
        isTick(m.tick) &&
        isText(m.playerId, MAX_ID_CHARS) &&
        isWireInput(m.frame)
        ? (m as ClientMessage)
        : null;
    case "player:report":
      return isText(m.roomId, MAX_ID_CHARS) && isText(m.playerId, MAX_ID_CHARS)
        ? (m as ClientMessage)
        : null;
    case "sync:hash":
      return isText(m.roomId, MAX_ID_CHARS) && isTick(m.tick) && isStr(m.hash) && m.hash.length <= 32
        ? (m as ClientMessage)
        : null;
    case "ping":
      return isNum(m.t) ? (m as ClientMessage) : null;
    default:
      return null;
  }
}

export function parseServerMessage(raw: string): ServerMessage | null {
  try {
    const msg = JSON.parse(raw) as ServerMessage;
    if (!msg || typeof msg !== "object" || !("type" in msg)) return null;
    return msg;
  } catch {
    return null;
  }
}

export function sendMessage(ws: { send(data: string): void }, msg: ClientMessage | ServerMessage): void {
  ws.send(JSON.stringify(msg));
}

/**
 * Manda un mensaje YA serializado.
 *
 * Existe para el reparto por sala: el bundle de cada tick es el MISMO objeto
 * para todos los de la sala, y serializarlo una vez por socket era repetir el
 * trabajo tantas veces como jugadores hubiera. Medido en la auditoria: en 3v3
 * son 240 `JSON.stringify` por segundo y por sala del mismo objeto, y crece con
 * el cuadrado (mas salas x mas jugadores).
 *
 * Va aparte de `sendMessage` a proposito, para que quede claro en el sitio de
 * llamada que la cadena se comparte y nadie intente mutar el objeto despues.
 */
export function sendRaw(ws: { send(data: string): void }, data: string): void {
  ws.send(data);
}
